import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Scissors, RefreshCw, Lock, ShieldCheck, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut, apiPost, formatTs } from './ops_helpers';

const MODE_BADGE = {
  off: 'bg-zinc-700 text-zinc-200',
  shadow: 'bg-sky-700 text-sky-100',
  active: 'bg-rose-700 text-rose-100',
};

const EVENT_BADGE = {
  SMART_EXIT_EVALUATED: 'bg-zinc-700',
  SMART_EXIT_SHADOW_MOVE_SL: 'bg-amber-600',
  SMART_EXIT_SHADOW_CLOSE: 'bg-emerald-700',
  SMART_EXIT_ERROR: 'bg-rose-700',
};

const ParamsDialog = ({ row, defaults, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({ ...defaults, ...(row.params || {}) });
  const [mode, setMode] = useState(row.mode || 'off');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await apiPut(`/api/ops/smart-exit/config/${row.deployment_id}`, {
        mode, enabled: mode !== 'off',
        rules: row.rules && row.rules.length ? row.rules : undefined,
        params,
        bot_id: row.bot_id || undefined,
      });
      toast.success('Config Smart Exit guardada');
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`smartexit-edit-${row.deployment_id}`}>
          Editar params
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">Smart Exit · {row.deployment_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            {['off', 'shadow'].map((m) => (
              <Button key={m} size="sm"
                variant={mode === m ? 'default' : 'outline'}
                onClick={() => setMode(m)}
                data-testid={`smartexit-mode-${m}-${row.deployment_id}`}>
                {m.toUpperCase()}
              </Button>
            ))}
            <Button size="sm" variant="outline" disabled
              title="Bloqueado en Fase 1 (SMART_EXIT_ALLOW_ACTIVE=False)"
              data-testid={`smartexit-mode-active-${row.deployment_id}`}>
              <Lock className="w-3 h-3 mr-1" /> ACTIVE 🔒
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {Object.keys(defaults).map((k) => (
              <div key={k} className="space-y-1">
                <Label className="text-xs text-zinc-400">{k}</Label>
                <Input
                  type="number" step="0.01"
                  value={params[k] ?? ''}
                  onChange={(e) => setParams((p) => ({ ...p, [k]: parseFloat(e.target.value) }))}
                  data-testid={`smartexit-param-${k}-${row.deployment_id}`}
                  className="bg-zinc-900 border-zinc-700 h-8"
                />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving}
            data-testid={`smartexit-save-${row.deployment_id}`}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SmartExitPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [events, setEvents] = useState([]);
  const [impact, setImpact] = useState(null);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const d = await apiGet('/api/ops/smart-exit/configs');
      setData(d);
    } catch (e) {
      toast.error(`Error cargando Smart Exit: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (row) => {
    setSelected(row.deployment_id);
    try {
      const [ev, imp] = await Promise.all([
        apiGet(`/api/ops/smart-exit/events?deployment_id=${row.deployment_id}&limit=30`),
        apiGet(`/api/ops/smart-exit/${row.deployment_id}/impact`),
      ]);
      setEvents(ev.events || []);
      setImpact(imp);
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    }
  };

  const setModeQuick = async (row, mode) => {
    try {
      if (row.configured) {
        await apiPost(`/api/ops/smart-exit/config/${row.deployment_id}/set-mode`, { mode });
      } else {
        await apiPut(`/api/ops/smart-exit/config/${row.deployment_id}`, {
          mode, enabled: mode !== 'off', params: {}, bot_id: row.bot_id || undefined,
        });
      }
      toast.success(`${row.deployment_id} → ${mode.toUpperCase()}`);
      fetchConfigs();
    } catch (e) {
      toast.error(`Error: ${e.message}`);
    }
  };

  useEffect(() => { fetchConfigs(); }, []);

  const rows = data?.deployments || [];
  const defaults = data?.default_params || {};

  return (
    <div className="space-y-4" data-testid="smart-exit-panel">
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-zinc-100 flex items-center gap-2">
            <Scissors className="w-5 h-5 text-sky-400" /> Smart Exit Framework
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge className={data?.allow_active ? 'bg-rose-700' : 'bg-zinc-700'}
              data-testid="smartexit-allow-active">
              {data?.allow_active ? 'ACTIVE HABILITADO' : 'ACTIVE bloqueado (Fase 1)'}
            </Badge>
            <Button size="sm" variant="outline" onClick={fetchConfigs} disabled={loading}
              data-testid="smartexit-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead>Deployment</TableHead>
                <TableHead>Bot / Estrategia</TableHead>
                <TableHead>Modo</TableHead>
                <TableHead>Reglas</TableHead>
                <TableHead>Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.deployment_id} className="border-zinc-800"
                  data-testid={`smartexit-row-${r.deployment_id}`}>
                  <TableCell className="font-mono text-xs text-zinc-300">{r.deployment_id}</TableCell>
                  <TableCell className="text-sm">
                    {r.bot_id || '—'} <span className="text-zinc-500">/ {r.strategy || '—'}</span>
                  </TableCell>
                  <TableCell>
                    <Badge className={MODE_BADGE[r.mode] || 'bg-zinc-700'}
                      data-testid={`smartexit-modebadge-${r.deployment_id}`}>
                      {(r.mode || 'off').toUpperCase()}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-zinc-400">
                    {(r.rules && r.rules.length) ? r.rules.join(' · ') : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => setModeQuick(r, 'off')}
                        data-testid={`smartexit-off-${r.deployment_id}`}>OFF</Button>
                      <Button size="sm" variant="outline" onClick={() => setModeQuick(r, 'shadow')}
                        data-testid={`smartexit-shadow-${r.deployment_id}`}>SHADOW</Button>
                      <ParamsDialog row={r} defaults={defaults} onSaved={fetchConfigs} />
                      <Button size="sm" variant="ghost" onClick={() => openDetail(r)}
                        data-testid={`smartexit-detail-${r.deployment_id}`}>
                        <Activity className="w-4 h-4 mr-1" /> Eventos
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-6">
                  {loading ? 'Cargando…' : 'Sin deployments en el catálogo de flota.'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selected && (
        <Card className="bg-zinc-900/60 border-zinc-800" data-testid="smartexit-detail-card">
          <CardHeader>
            <CardTitle className="text-zinc-100 flex items-center gap-2 text-base">
              <ShieldCheck className="w-4 h-4 text-sky-400" /> {selected}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {impact && (
              <div className="flex flex-wrap gap-4 text-sm" data-testid="smartexit-impact">
                <span className="text-zinc-400">Impacto shadow (estimación):</span>
                <span>Intervenciones: <b className="text-zinc-100">{impact.interventions}</b></span>
                <span>R neto est.: <b className={impact.r_net_est >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{impact.r_net_est}</b></span>
                <span>Mejora: <b className="text-zinc-100">{impact.pct_better}%</b></span>
                <Badge className="bg-zinc-700">ESTIMACIÓN</Badge>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead>Hora</TableHead>
                  <TableHead>Evento</TableHead>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Regla</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Impacto est.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((e, i) => (
                  <TableRow key={i} className="border-zinc-800" data-testid={`smartexit-event-${i}`}>
                    <TableCell className="text-xs text-zinc-400">{formatTs(e.ts)}</TableCell>
                    <TableCell><Badge className={EVENT_BADGE[e.event_type] || 'bg-zinc-700'}>{(e.event_type || '').replace('SMART_EXIT_', '')}</Badge></TableCell>
                    <TableCell className="text-xs">{e.ticket || '—'}</TableCell>
                    <TableCell className="text-xs">{e.rule || '—'}</TableCell>
                    <TableCell className="text-xs text-zinc-400">{e.reason || '—'}</TableCell>
                    <TableCell className="text-xs">
                      {e.impact ? `${e.impact.r_saved_est ?? e.impact.r_delta_est ?? '—'}R (${e.impact.verdict || ''})` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {events.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-6">Sin eventos todavía.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SmartExitPanel;
